import React, { useState, useEffect } from 'react';
import { 
  Pill, ShieldCheck, ArrowLeft, Home, Sparkles, Download, 
  Copy, Check, FileText, Upload, X, RefreshCw, AlertCircle, 
  Lock, Crown, Activity, Stethoscope, CheckCircle2, HelpCircle, Info
} from 'lucide-react';
import { autoDownloadReport } from '../utils/autoDownloader';
import { formatPriceWithCurrency, Currency } from '../utils/i18n';

interface PharmaAdvisorAgentViewProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  currentUserEmail?: string;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
  currency?: Currency;
}

interface AttachedFile {
  file: File;
  text?: string;
  extracted?: boolean;
  loading?: boolean;
}

const DEMO_QUERY_GUIDE = `Препарат: Амлодипін + Вальсарта комбінована терапія (наприклад, Ексфорж / Вамлосет).
Мета запиту: Повний фармакологічний довідник препарату.
Параметри пацієнта: Дорослий, 54 роки, есенціальна артеріальна гіпертензія 2 ступеня.
Потрібні відомості:
1. МНН (Міжнародна непатентована назва) та класифікація ATC (код C09DX01).
2. Склад, дозувальні форми (5/80 мг, 5/160 мг, 10/160 мг).
3. Механізм дії (дигідропіридиновий блокатор кальцієвих каналів + антагоніст рецепторів ангіотензину II).
4. Показання, детальна схема прийому, коригування доз при нирковій/печінковій патології.
5. Побічні ефекти (набряки кісточок, запаморочення, гіперкаліємія) та правила відпуску (за рецептом).`;

const DEMO_QUERY_ANALOGS = `Препарат для замінування: Ксарелто (Rivaroxaban / Рівароксабан 10 мг, 15 мг, 20 мг).
Код ATC: B01AF01 (Прямі інгібітори фактора Xa).
Мета запиту: Пошук еквівалентних аналогів та генериків у Державному реєстрі лікарських засобів України (ДЛУ).
Вимоги до аналізу:
1. Перелік зареєстрованих в Україні генериків з аналогічною діючою речовиною (Ріварокс, Ксабікон, Ріваксан, Вазоксан тощо).
2. Терапевтична еквівалентність та фармацевтична якість (GMP сертифікація виробників).
3. Цінове зіставлення (орієнтовна вартість у гривнях в аптечних мережах України).
4. Альтернативи з інших підкласів ПОАК (Апіксабан/Еліквіс, Дабігатран/Прадакса) при непереносимості.`;

const DEMO_QUERY_INTERACTION = `Терапевтична схема пацієнта (похідні ліки):
1. Варфарин (Warfarin 2.5 мг) — пероральний антикоагулянт.
2. Ацетилосаліцилова кислота (Магнікор / Аспірин Кардіо 75 мг) — антиагрегант.
3. Ібупрофен (Ibuprofen 400 мг) — НПЗЗ при суглобовому болю.
4. Омепразол (Omeprazole 20 мг) — інгібітор протонної помпи.

Мета запиту: Повний аналіз міжлікарських взаємодій (Drug-Drug Interactions), фармзастереження та рекомендації з коригування scheme.`;

const DEMO_QUERY_SELECTION = `Симптоматика та анамнез пацієнта:
- Вік: 62 роки.
- Діагноз: Цукровий діабет 2 типу + Початкова діабетична нефропатія + Артеріальна гіпертензія (АТ 155/95 мм рт. ст.).
- Супутні скарги: Періодичний нічний кашель, набряки нижніх кінцівок.
- Алергії: Виражена алергічна реакція на пеніциліни.

Мета запиту: Індивідуальний підбір першої лінії антигіпертензивних та органопротективних препаратів з урахуванням органопротекції нирок (інгібітори АПФ / БРА / інгібітори НЗКТ-2) та застережень щодо безпеки.`;

export default function PharmaAdvisorAgentView({
  language,
  onBack,
  onGoHome,
  currentUserEmail,
  useEnterpriseContext,
  enterpriseName,
  currency = 'UAH'
}: PharmaAdvisorAgentViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isAdmin = ['bbass776@gmail.com', 'irinacosmos777@gmail.com'].includes(currentUserEmail?.toLowerCase() || '');

  // States
  const [taskMode, setTaskMode] = useState<'guide' | 'analogs' | 'interaction' | 'selection' | 'pharmacoeconomics'>('guide');
  const [medicineName, setMedicineName] = useState('');
  const [atcCode, setAtcCode] = useState('');
  const [patientProfile, setPatientProfile] = useState('adult');
  const [inputQueryText, setInputQueryText] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [reportResult, setReportResult] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);

  useEffect(() => {
    if (reportResult && typeof reportResult === 'string' && reportResult.trim().length > 0 && !reportResult.startsWith('⚠️ ПОМИЛКА')) {
      autoDownloadReport(reportResult, 'Фармакологічний Довідник & Звіт', 'pharma-advisor');
    }
  }, [reportResult]);



  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const newFiles = Array.from(e.target.files).map(file => ({ file, loading: true }));
    setAttachedFiles(prev => [...prev, ...newFiles]);

    for (let i = 0; i < newFiles.length; i++) {
      const fileObj = newFiles[i];
      const formData = new FormData();
      formData.append('file', fileObj.file);

      try {
        const res = await fetch('/api/extract-text', {
          method: 'POST',
          body: formData
        });
        if (res.ok) {
          const data = await res.json();
          setAttachedFiles(prev => prev.map(item => item.file === fileObj.file ? { ...item, text: data.text || '', extracted: true, loading: false } : item));
        } else {
          setAttachedFiles(prev => prev.map(item => item.file === fileObj.file ? { ...item, loading: false } : item));
        }
      } catch {
        setAttachedFiles(prev => prev.map(item => item.file === fileObj.file ? { ...item, loading: false } : item));
      }
    }
  };

  const handleRemoveFile = (fileToRemove: File) => {
    setAttachedFiles(prev => prev.filter(item => item.file !== fileToRemove));
  };

  const handleRunAnalysis = async () => {
    if (!inputQueryText.trim() && !medicineName.trim() && attachedFiles.length === 0) {
      setErrorMsg(isUk ? 'Будь ласка, введіть назву препарату, опис запиту або завантажте рецепт/інструкцію.' : 'Please enter medicine name, query description or attach prescription/instruction file.');
      return;
    }

    setIsProcessing(true);
    setErrorMsg(null);

    let fullPrompt = `Режим завдання: ${taskMode.toUpperCase()}\n`;
    if (medicineName) fullPrompt += `Назва препарату / МНН: ${medicineName}\n`;
    if (atcCode) fullPrompt += `Код ATC / Категорія: ${atcCode}\n`;
    if (patientProfile) fullPrompt += `Профіль пацієнта: ${patientProfile}\n`;
    if (inputQueryText) fullPrompt += `\nДеталі запиту:\n${inputQueryText.trim()}\n`;

    attachedFiles.forEach(item => {
      if (item.text) {
        fullPrompt += `\n\n--- ЗМІСТ ФАЙЛУ (${item.file.name}) ---\n${item.text}`;
      }
    });

    try {
      const res = await fetch('/api/run-templated-crew', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          crewName: 'pharma_advisor_crew',
          useEnterpriseContext: false,
          inputs: {
            task_mode: taskMode,
            medicine_name: medicineName || 'Не вказано',
            atc_code: atcCode || 'Авто-визначення',
            patient_profile: patientProfile,
            query_details: fullPrompt,
            enterprise_context: useEnterpriseContext && enterpriseName ? `Компанія: ${enterpriseName}` : ''
          }
        })
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();
      if (data.report || data.result) {
        setReportResult(data.report || data.result);
      } else if (data.jobId) {
        // Poll status if background job
        pollJobStatus(data.jobId);
      } else {
        throw new Error(data.message || 'Не вдалося отримати звіт');
      }
    } catch (err: any) {
      console.error('Pharma Agent Error:', err);
      // Direct fallback call to server API if crew service is unreachable
      runDirectFallback(fullPrompt);
    }
  };

  const runDirectFallback = async (prompt: string) => {
    try {
      const res = await fetch('/api/pharma-advisor/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt,
          mode: taskMode,
          language
        })
      });
      if (res.ok) {
        const data = await res.json();
        setReportResult(data.report || data.result);
      } else {
        const errData = await res.json().catch(() => ({}));
        setErrorMsg(errData.message || 'Помилка під час формування фармацевтичного висновку.');
      }
    } catch (e: any) {
      setErrorMsg('Помилка з\'єднання з сервером. Спробуйте повторно.');
    } finally {
      setIsProcessing(false);
    }
  };

  const pollJobStatus = async (jobId: string) => {
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`/api/crew-status/${jobId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.status === 'completed') {
            clearInterval(interval);
            setReportResult(data.result || data.report);
            setIsProcessing(false);
          } else if (data.status === 'failed') {
            clearInterval(interval);
            setErrorMsg(data.error || 'Помилка виконання фармакологічного аудиту.');
            setIsProcessing(false);
          }
        }
      } catch {
        clearInterval(interval);
        setIsProcessing(false);
        setErrorMsg('Помилка перевірки статусу виконання.');
      }
    }, 2500);
  };

  const handleApplyDemo = (demoType: 'guide' | 'analogs' | 'interaction' | 'selection') => {
    if (demoType === 'guide') {
      setTaskMode('guide');
      setMedicineName('Амлодипін + Вальсартан (Ексфорж / Вамлосет)');
      setAtcCode('C09DX01 - Антагоністи ангіотензину II та блокатори кальцієвих каналів');
      setPatientProfile('adult');
      setInputQueryText(DEMO_QUERY_GUIDE);
    } else if (demoType === 'analogs') {
      setTaskMode('analogs');
      setMedicineName('Ксарелто (Rivaroxaban / Рівароксабан 10/15/20 мг)');
      setAtcCode('B01AF01 - Прямі інгібітори фактора Xa');
      setPatientProfile('adult');
      setInputQueryText(DEMO_QUERY_ANALOGS);
    } else if (demoType === 'interaction') {
      setTaskMode('interaction');
      setMedicineName('Варфарин + Аспірин + Ібупрофен + Омепразол');
      setAtcCode('B01AA03, B01AC06, M01AE01');
      setPatientProfile('elderly');
      setInputQueryText(DEMO_QUERY_INTERACTION);
    } else if (demoType === 'selection') {
      setTaskMode('selection');
      setMedicineName('Підбір гіпотензивної та цукрознижувальної терапії');
      setAtcCode('C09 / A10 - Серцево-судинна & Метаболічна терапія');
      setPatientProfile('diabetic');
      setInputQueryText(DEMO_QUERY_SELECTION);
    }
  };

  const handleCopyReport = () => {
    if (!reportResult) return;
    navigator.clipboard.writeText(reportResult);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadDocx = async () => {
    if (!reportResult) return;
    setDownloadingDocx(true);
    try {
      const res = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          markdownText: reportResult,
          documentTitle: `Фармацевтичний_Висновок_${medicineName ? medicineName.replace(/\s+/g, '_') : 'Довідник'}`
        })
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Pharma_Report_${Date.now()}.docx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Не вдалося згенерувати Word-документ.');
      }
    } catch (e) {
      console.error('Docx download error:', e);
      alert('Помилка експорту у Word.');
    } finally {
      setDownloadingDocx(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in pb-12 font-mono max-w-6xl mx-auto">
      {/* Top Header Navigation */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-teal-500/30 rounded-xl p-4 text-white shadow-lg">
        <div className="flex items-center gap-3">
          <button
            onClick={onGoHome || onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-teal-300 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-teal-500/40"
          >
            <Home className="w-4 h-4" />
            <span>{isPl ? '🏠 Strona Główna' : isUk ? '🏠 На головну' : '🏠 Home'}</span>
          </button>
          <button
            onClick={onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-teal-300 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-teal-500/40"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{isPl ? '← Powrót do Centrum Farmacji' : isUk ? '← До Фармацевтичного Центру' : '← Back to Pharma Hub'}</span>
          </button>
        </div>

        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-teal-500/20 text-teal-300 border border-teal-400/40 rounded-md flex items-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5 text-teal-300" />
            {isPl ? '🛡️ COMPLIANCE FARMACEUTYCZNY & ChPL' : '🛡️ ФАРМАЦЕВТИЧНИЙ КОМПЛАЄНС & ДРЛЗ'}
          </span>
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-md">
            ✨ Gemini 3.7 Flash
          </span>
        </div>
      </div>

      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-slate-950 via-teal-950 to-slate-900 border border-teal-500/50 rounded-2xl p-6 sm:p-8 text-white shadow-2xl overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-teal-500/30 text-teal-300 border border-teal-400/40 rounded flex items-center gap-1">
              <Pill className="w-3 h-3 text-teal-300" />
              {isPl ? 'PRZEWODNIK LEKÓW ChPL • KOD ATC / INN • INTERAKCJE • ZAMIENNIKI' : 'ДОВІДНИК ДЛУ • ATC / МНН • АНАЛОГИ • ВЗАЄМОДІЇ • ДСТУ 4163:2020'}
            </span>
            <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded">
              {isPl ? '🎁 1. start ZA DARMO (100% FREE)' : '🎁 1-й запуск БЕЗКОШТОВНО (100% FREE)'}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-teal-500/20 border border-teal-400/40 flex items-center justify-center text-teal-300">
              <Pill className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight uppercase bg-gradient-to-r from-teal-200 via-cyan-100 to-teal-400 bg-clip-text text-transparent">
                {isPl ? 'PRZEWODNIK LEKÓW & AUDYT FARMAKOLOGICZNY' : isUk ? 'ДОВІДНИК ЛІКІВ & ФАРМАКОЛОГІЧНИЙ АУДИТ' : 'DRUG DIRECTORY & PHARMA AUDIT'}
              </h1>
              <p className="text-xs text-slate-300">
                {isPl
                  ? 'Autonomiczny system audytu farmakologicznego, porównywania zamienników, interakcji i kalkulacji dawkowania'
                  : isUk
                  ? 'Автономна система фармакологічного аудиту, порівняння аналогів, взаємодій та розрахунку дозування'
                  : 'Autonomous drug intelligence, interaction analysis, generic replacement & dosage calculator'}
              </p>
            </div>
          </div>

          {/* Pricing & Economics Card */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 text-xs">
            <div className="bg-slate-900/80 border border-teal-500/30 rounded-xl p-3 flex items-center justify-between">
              <span className="text-slate-400 font-medium">{isUk ? 'Модель ШІ:' : isPl ? 'Model AI:' : 'AI Model:'}</span>
              <span className="font-bold text-teal-300">✨ Gemini 3.7 Flash</span>
            </div>
            <div className="bg-slate-900/80 border border-teal-500/30 rounded-xl p-3 flex items-center justify-between">
              <span className="text-slate-400 font-medium">{isUk ? 'Підписка:' : isPl ? 'Subskrypcja:' : 'Subscription:'}</span>
              <span className="font-bold text-teal-300">{formatPriceWithCurrency(490, currency)} / 30 {isUk ? 'запитів' : isPl ? 'zapytań' : 'req.'}</span>
            </div>
            <div className="bg-slate-900/80 border border-teal-500/30 rounded-xl p-3 flex items-center justify-between">
              <span className="text-slate-400 font-medium">{isUk ? 'Разово / Маржа:' : isPl ? 'Pojedynczo:' : 'Single Run:'}</span>
              <span className="font-bold text-emerald-400">{formatPriceWithCurrency(25, currency)} (98.5% Margin)</span>
            </div>
          </div>
        </div>
      </div>

      {/* 1-Click Demo Buttons Bar */}
      <div className="bg-slate-900 border border-teal-500/30 rounded-2xl p-5 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-xs font-extrabold text-teal-300 uppercase flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span>{isPl ? '⚡ Wypróbuj na przykładach demo w 1 kliknięcie:' : isUk ? '⚡ Спробувати на демо-прикладах в 1 клік:' : '⚡ 1-Click Demo Examples:'}</span>
          </span>
          <span className="text-[10px] text-slate-400">{isPl ? 'Kliknij, aby automatycznie wypełnić realistycznymi danymi' : isUk ? 'Натисніть для авто-заповнення реалістичними даними' : 'Click to auto-fill with realistic data'}</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2.5">
          <button
            type="button"
            onClick={() => handleApplyDemo('guide')}
            className="p-3 bg-slate-950 hover:bg-slate-800 border border-teal-500/40 hover:border-teal-400 rounded-xl text-left transition-all cursor-pointer space-y-1 group"
          >
            <div className="text-xs font-bold text-teal-200 group-hover:text-white flex items-center gap-1">
              <span>💊 Довідник препарату</span>
            </div>
            <div className="text-[10px] text-slate-400 line-clamp-2">
              Амлодипін + Вальсартан (Ексфорж) — МНН, ATC C09DX01, дози та показання
            </div>
          </button>

          <button
            type="button"
            onClick={() => handleApplyDemo('analogs')}
            className="p-3 bg-slate-950 hover:bg-slate-800 border border-teal-500/40 hover:border-teal-400 rounded-xl text-left transition-all cursor-pointer space-y-1 group"
          >
            <div className="text-xs font-bold text-teal-200 group-hover:text-white flex items-center gap-1">
              <span>🔄 Підбір аналогів МНН</span>
            </div>
            <div className="text-[10px] text-slate-400 line-clamp-2">
              Ксарелто (Рівароксабан) — пошук генериків у ДЛУ та цінове порівняння
            </div>
          </button>

          <button
            type="button"
            onClick={() => handleApplyDemo('interaction')}
            className="p-3 bg-slate-950 hover:bg-slate-800 border border-teal-500/40 hover:border-teal-400 rounded-xl text-left transition-all cursor-pointer space-y-1 group"
          >
            <div className="text-xs font-bold text-teal-200 group-hover:text-white flex items-center gap-1">
              <span>⚠️ Взаємодія ліків</span>
            </div>
            <div className="text-[10px] text-slate-400 line-clamp-2">
              Варфарин + Аспірин + Ібупрофен — аналіз сумісності та кровотеч
            </div>
          </button>

          <button
            type="button"
            onClick={() => handleApplyDemo('selection')}
            className="p-3 bg-slate-950 hover:bg-slate-800 border border-teal-500/40 hover:border-teal-400 rounded-xl text-left transition-all cursor-pointer space-y-1 group"
          >
            <div className="text-xs font-bold text-teal-200 group-hover:text-white flex items-center gap-1">
              <span>🧑‍⚕️ Підбір за анамнезом</span>
            </div>
            <div className="text-[10px] text-slate-400 line-clamp-2">
              Цукровий діабет + Нефропатія + Гіпертензія — органопротективний підбір
            </div>
          </button>
        </div>
      </div>

      {/* Main Input Form */}
      <div className="bg-slate-900 border border-teal-500/30 rounded-2xl p-6 space-y-6 shadow-xl">
        {/* Mode Selector */}
        <div className="space-y-2">
          <label className="text-xs font-extrabold uppercase text-teal-300 flex items-center gap-2">
            <span>Оберіть тип фармацевтичного завдання:</span>
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
            {[
              { id: 'guide', label: '💊 Довідник', desc: 'Склад & МНН' },
              { id: 'analogs', label: '🔄 Аналоги', desc: 'Генерики & ATC' },
              { id: 'interaction', label: '⚠️ Сумісність', desc: 'Взаємодії' },
              { id: 'selection', label: '🧑‍⚕️ Підбір ліків', desc: 'За анамнезом' },
              { id: 'pharmacoeconomics', label: '📑 Доступні ліки', desc: 'Реімбурсація' }
            ].map(m => (
              <button
                key={m.id}
                type="button"
                onClick={() => setTaskMode(m.id as any)}
                className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                  taskMode === m.id
                    ? 'bg-teal-600/30 border-teal-400 text-white shadow-lg shadow-teal-500/20'
                    : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700'
                }`}
              >
                <div className="text-xs font-bold">{m.label}</div>
                <div className="text-[10px] opacity-75">{m.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Input Fields */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <label className="text-xs font-extrabold uppercase text-slate-300">
              Назва препарату / МНН:
            </label>
            <input
              type="text"
              list="medicine-recommendations"
              value={medicineName}
              onChange={e => setMedicineName(e.target.value)}
              placeholder="Наприклад: Аторвастатин, Еналаприл, Ксарелто..."
              className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-teal-400"
            />
            <datalist id="medicine-recommendations">
              <option value="Амлодипін + Вальсартан" />
              <option value="Ксарелто (Рівароксабан 10/15/20 мг)" />
              <option value="Варфарин (Warfarin 2.5 мг)" />
              <option value="Лізиноприл (Lisinopril 10/20 мг)" />
              <option value="Метформін (Metformin 850/1000 мг)" />
              <option value="Бісопролол (Bisoprolol 5 мг)" />
              <option value="Азитроміцин (Azithromycin 500 мг)" />
            </datalist>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-extrabold uppercase text-slate-300">
              АТС-код / Фарм-група:
            </label>
            <input
              type="text"
              list="atc-recommendations"
              value={atcCode}
              onChange={e => setAtcCode(e.target.value)}
              placeholder="Наприклад: C09AA02, B01AF01, J01FA10..."
              className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-teal-400"
            />
            <datalist id="atc-recommendations">
              <option value="C09 - Інгібітори ангіотензинперетворювального ферменту (АПФ)" />
              <option value="B01 - Антитромботичні засоби (ПОАК / Антикоагулянти)" />
              <option value="A10 - Препарати для лікування цукрового діабету" />
              <option value="J01 - Антибактеріальні засоби для системного застосування" />
              <option value="N02 - Анальгетики та антипіретики" />
            </datalist>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-extrabold uppercase text-slate-300">
              Профіль пацієнта / Категорія:
            </label>
            <select
              value={patientProfile}
              onChange={e => setPatientProfile(e.target.value)}
              className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-teal-400 cursor-pointer"
            >
              <option value="adult">Дорослий пацієнт (18-65 років)</option>
              <option value="pediatric">Педіатрія (Діти / Підлітки)</option>
              <option value="elderly">Похилий вік (65+ років)</option>
              <option value="pregnancy">Вагітність / Лактація</option>
              <option value="renal">Ниркова недостатність (ХХН / eGFR &lt; 60)</option>
              <option value="hepatic">Печінкова недостатність (Child-Pugh A/B/C)</option>
              <option value="diabetic">Цукровий діабет + Метаболічний синдром</option>
            </select>
          </div>
        </div>

        {/* Detailed Text Area */}
        <div className="space-y-1.5">
          <label className="text-xs font-extrabold uppercase text-slate-300 flex items-center justify-between">
            <span>Опис симптоматики, рецепту чи схеми лікування:</span>
            <span className="text-[10px] text-slate-400 font-normal">Детальний запит або клінічна ситуація</span>
          </label>
          <textarea
            rows={5}
            value={inputQueryText}
            onChange={e => setInputQueryText(e.target.value)}
            placeholder="Введіть перелік препаратів для аналізу сумісності, скарги пацієнта, дозування або питання щодо відпуску та аналогів..."
            className="w-full bg-slate-950 border border-slate-700 rounded-xl p-4 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-teal-400 leading-relaxed font-sans"
          />
        </div>

        {/* Multi-File Upload Component */}
        <div className="space-y-2 pt-2 border-t border-slate-800">
          <label className="text-xs font-extrabold uppercase text-teal-300 flex items-center gap-2">
            <Upload className="w-4 h-4 text-teal-400" />
            <span>Прикріпити рецепт, інструкцію або виписку (PDF, DOCX, XLSX, TXT, фото):</span>
          </label>

          <div className="flex items-center gap-4 flex-wrap">
            <label className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-teal-300 text-xs font-bold uppercase rounded-xl transition-all cursor-pointer border border-teal-500/40 inline-flex items-center gap-2">
              <Upload className="w-4 h-4" />
              <span>Обрати файли з ПК / смартфона</span>
              <input
                type="file"
                multiple
                accept=".pdf,.docx,.xlsx,.txt,.csv,.json,.jpg,.jpeg,.png,.webp"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
            <span className="text-[10px] text-slate-400">
              Підтримуються виписки, аналізи, фото упаковок чи скани рецептів
            </span>
          </div>

          {attachedFiles.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2">
              {attachedFiles.map((f, i) => (
                <div
                  key={i}
                  className="px-3 py-1.5 bg-slate-950 border border-teal-500/40 rounded-lg text-xs text-slate-200 flex items-center gap-2"
                >
                  <FileText className="w-3.5 h-3.5 text-teal-400" />
                  <span className="max-w-[160px] truncate">{f.file.name}</span>
                  {f.loading ? (
                    <RefreshCw className="w-3 h-3 text-amber-400 animate-spin" />
                  ) : f.extracted ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                  ) : null}
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(f.file)}
                    className="hover:text-red-400 text-slate-500 ml-1 cursor-pointer"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Error Message */}
        {errorMsg && (
          <div className="p-4 bg-red-500/10 border border-red-500/40 rounded-xl text-red-300 text-xs flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Action Button */}
        <div className="pt-2">
          <button
            type="button"
            disabled={isProcessing}
            onClick={handleRunAnalysis}
            className="w-full py-4 bg-gradient-to-r from-teal-600 via-cyan-600 to-teal-500 hover:from-teal-500 hover:to-cyan-500 text-slate-950 font-extrabold text-sm uppercase rounded-xl transition-all shadow-xl shadow-teal-600/20 flex items-center justify-center gap-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.01]"
          >
            {isProcessing ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                <span>Триває аналіз ДЛУ та формування фармацевтичного висновку...</span>
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                <span>🚀 Сформувати Фармацевтичний Висновок (Gemini 3.7 Flash)</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Generated Report Viewer */}
      {reportResult && (
        <div className="bg-slate-900 border border-teal-500/40 rounded-2xl p-6 sm:p-8 space-y-6 shadow-2xl animate-fade-in">
          <div className="flex items-center justify-between gap-4 flex-wrap pb-4 border-b border-slate-800">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-1 bg-teal-500/20 text-teal-300 border border-teal-400/30 rounded-md text-[10px] font-extrabold uppercase flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                ФАРМАЦЕВТИЧНИЙ ВИСНОВОК ЗФОРМОВАНО
              </span>
              <span className="text-xs text-slate-400">ДСТУ 4163:2020 Compliant</span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCopyReport}
                className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-teal-300 text-xs font-bold uppercase rounded-xl transition-all flex items-center gap-1.5 border border-slate-700 cursor-pointer"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                <span>{copied ? 'Скопійовано' : 'Копіювати'}</span>
              </button>

              <button
                type="button"
                disabled={downloadingDocx}
                onClick={handleDownloadDocx}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-500 text-slate-950 font-extrabold text-xs uppercase rounded-xl transition-all flex items-center gap-2 shadow-md shadow-teal-600/20 cursor-pointer disabled:opacity-50"
              >
                {downloadingDocx ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span>📥 Завантажити Word (.docx)</span>
              </button>
            </div>
          </div>

          <div className="prose prose-invert max-w-none text-xs sm:text-sm text-slate-200 leading-relaxed font-sans space-y-4 whitespace-pre-line">
            {reportResult}
          </div>

          <div className="p-4 bg-slate-950 border border-amber-500/30 rounded-xl text-[11px] text-amber-300 flex items-start gap-2">
            <Info className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <span>
              <strong>Застереження клінічної фармакології:</strong> Згенерований ШІ-висновок носить консультативно-довідковий характер для фахівців. Остаточне призначення, корекція доз та відпуск рецептурних препаратів здійснюється строго кваліфікованим лікарем та фармацевтом згідно із законодавством України.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
